/**
 * AddTargetDialog Component
 *
 * Dialog for creating new pipeline targets with natural language input.
 *
 * Date: 2026-03-11
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Sparkles, Check, X } from 'lucide-react';
import {
  parseIntent,
  createTarget,
  getSites,
  getCategoriesForSite,
  getPriorityLabel,
  type ParsedIntent,
  type Site,
  type Category,
} from '@/services/pipelineTrackerService';

interface AddTargetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function AddTargetDialog({ open, onOpenChange, onSuccess }: AddTargetDialogProps) {
  const [naturalLanguage, setNaturalLanguage] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [parsed, setParsed] = useState<ParsedIntent | null>(null);

  // Manual entry state
  const [sites, setSites] = useState<Site[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<number[]>([]);
  const [name, setName] = useState('');

  // Load sites on mount
  useEffect(() => {
    if (open) {
      loadSites();
    }
  }, [open]);

  // Load categories when site changes
  useEffect(() => {
    if (selectedSiteId) {
      loadCategories(selectedSiteId);
    }
  }, [selectedSiteId]);

  // Update form when parsed result changes
  useEffect(() => {
    if (parsed) {
      if (parsed.siteId) setSelectedSiteId(parsed.siteId);
      if (parsed.categoryPaths) setSelectedCategories(parsed.categoryPaths);
      if (parsed.priorityLevels) setSelectedPriorities(parsed.priorityLevels);
      if (parsed.generatedName) setName(parsed.generatedName);
    }
  }, [parsed]);

  const loadSites = async () => {
    try {
      const result = await getSites();
      if (result.success) {
        setSites(result.sites);
      }
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  };

  const loadCategories = async (siteId: string) => {
    try {
      const result = await getCategoriesForSite(siteId);
      if (result.success) {
        setCategories(result.categories);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleParseIntent = async () => {
    if (!naturalLanguage.trim()) return;

    setIsParsing(true);
    try {
      const result = await parseIntent(naturalLanguage);
      if (result.success) {
        setParsed(result.parsed);
      }
    } catch (error) {
      console.error('Failed to parse intent:', error);
    } finally {
      setIsParsing(false);
    }
  };

  const handleCreate = async () => {
    if (!name || !selectedSiteId) return;

    setIsCreating(true);
    try {
      const result = await createTarget({
        name,
        description: naturalLanguage || undefined,
        siteId: selectedSiteId,
        categoryPaths: selectedCategories,
        priorityLevels: selectedPriorities,
      });

      if (result.success) {
        onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error('Failed to create target:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setNaturalLanguage('');
    setParsed(null);
    setSelectedSiteId('');
    setSelectedCategories([]);
    setSelectedPriorities([]);
    setName('');
    onOpenChange(false);
  };

  const togglePriority = (level: number) => {
    setSelectedPriorities(prev =>
      prev.includes(level)
        ? prev.filter(p => p !== level)
        : [...prev, level].sort()
    );
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Content Target</DialogTitle>
          <DialogDescription>
            Describe what you want to track in natural language, or fill in the form manually.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Natural Language Input */}
          <div className="space-y-2">
            <Label>Describe what you want to track</Label>
            <div className="flex gap-2">
              <Textarea
                placeholder="e.g., All SuperTop and Top products for Running Shoes on RunnerInn"
                value={naturalLanguage}
                onChange={(e) => setNaturalLanguage(e.target.value)}
                className="flex-1"
                rows={2}
              />
              <Button
                onClick={handleParseIntent}
                disabled={isParsing || !naturalLanguage.trim()}
                className="shrink-0"
              >
                {isParsing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                <span className="ml-2">Parse</span>
              </Button>
            </div>
          </div>

          {/* Parsed Result Preview */}
          {parsed && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
              <p className="text-sm font-medium text-blue-800">Parsed Criteria</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Site:</span>
                  <span className="font-medium">{parsed.siteName || 'Not detected'}</span>
                  {parsed.siteId ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <X className="h-4 w-4 text-red-500" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Categories:</span>
                  <span className="font-medium">
                    {parsed.categoryKeywords?.length > 0
                      ? parsed.categoryKeywords.join(', ')
                      : 'All'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Priority:</span>
                  <span className="font-medium">
                    {parsed.priorityLevels?.length > 0
                      ? parsed.priorityLevels.map(getPriorityLabel).join(', ')
                      : 'All'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Products:</span>
                  <span className="font-medium">~{parsed.matchedProducts?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-4">Confirm or Edit Criteria</p>

            {/* Site Selection */}
            <div className="space-y-2 mb-4">
              <Label>Site *</Label>
              <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Priority Levels */}
            <div className="space-y-2 mb-4">
              <Label>Priority Levels</Label>
              <div className="flex gap-2">
                {[1, 2, 3].map((level) => (
                  <Badge
                    key={level}
                    variant={selectedPriorities.includes(level) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => togglePriority(level)}
                  >
                    {getPriorityLabel(level)}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                Leave empty to track all priority levels
              </p>
            </div>

            {/* Categories */}
            {selectedSiteId && categories.length > 0 && (
              <div className="space-y-2 mb-4">
                <Label>Categories (optional)</Label>
                <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1">
                  {categories.slice(0, 20).map((cat) => (
                    <div
                      key={cat.category}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={selectedCategories.includes(cat.category)}
                        onCheckedChange={() => toggleCategory(cat.category)}
                      />
                      <span className="flex-1 truncate">{cat.category}</span>
                      <span className="text-gray-400">
                        {cat.product_count.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
                {categories.length > 20 && (
                  <p className="text-xs text-gray-500">
                    Showing top 20 categories by product count
                  </p>
                )}
              </div>
            )}

            {/* Target Name */}
            <div className="space-y-2">
              <Label>Target Name *</Label>
              <Input
                placeholder="e.g., RunnerInn - Running Shoes - SuperTop & Top"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isCreating || !name || !selectedSiteId}
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Target'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
